import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getEChartsTooltipTheme, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isEmptyWidgetData, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseSeasonality {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private currentInterval: string = '1w';

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.attachStyles();
    this.createWidget();
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let seasonalityWidgetIndex = 0;
    const widgetIndex = seasonalityWidgetIndex++;
    const symbolOptions = this.fetchSymbols()
      .map(s => `<option value="${s.value}">${s.label}</option>`).join('');
    const intervalOptions = `
    <option value="1w">Weekly</option>
    <option value="1m">Monthly</option>
  `;

   let themeClass = 'defaultDarkMode';
let dataTheme = 'default-dark';

if (this.config.theme === 'white') {
  themeClass = 'whiteMode';
  dataTheme = 'white';
} else if (this.config.theme === 'black') {
  themeClass = 'blackMode';
  dataTheme = 'black';
} else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}"  data-testing-id="seasonality-widget-${widgetIndex}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Seasonality</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
              ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                              <div class="trader-sm-card" id="selected-symbol">
                              ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
                              </div>
                              <span id="symbol-dropdown-placeholder"></span>
                              </div>`}
              <span></span>
              <div class="item">
                  <select class="interval-dropdown">
                ${intervalOptions}
              </select>
              </div>
            </div>

            <div class="chartDv" id="seasonalityContainer" style="height: 400px;">
            </div>

          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    dropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;

    const intervalDropdown = this.shadowRoot.querySelector('.interval-dropdown') as HTMLSelectElement;
    intervalDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeInterval(value);
    });
  }


  private changeInterval(interval: string): void {
    this.currentInterval = interval;
    this.emit('intervalChange', { interval });
    this.setupChart(this.config.symbol!, this.currentInterval);
  }


  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol: string, interval: string = this.currentInterval): Promise<void> {
    try {
      const assetType = 'stocks'; // you can make this dynamic later if needed
      let apiUrl = '';

      if (interval === '1m') {
        apiUrl = `${APIURL}/v1/etl_payload/calculate_monthly_green_probabilities?ticker_1=${symbol}&asset_type=${assetType}`;
      } else {
        apiUrl = `${APIURL}/v1/etl_payload/calculate_green_day_probabilities?ticker_1=${symbol}&asset_type=${assetType}`;
      }

      const response = await apiGet({ url: apiUrl });
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      const probabilities =
        data?.Probability ||
        data ||
        {};

      const months = Object.keys(probabilities);
      const values = Object.values(probabilities).map((value: any) =>
        Math.round(value * 100)
      );

      this.currentData = {
        symbol,
        interval,
        months,
        values,
      };

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error('Error setting up chart:', err);
    }
  }


  private updateDisplay(): void {
    const container = this.shadowRoot.querySelector('#seasonalityContainer') as HTMLElement;
    if (!container) return;
    if (this.currentData == null) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (isEmptyWidgetData(this.currentData?.values)) {
      container.innerHTML = getNoDataHTML();
      return;
    }
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderSeasonality();
  }

  private renderSeasonality(): void {
    const container = this.shadowRoot.querySelector('#seasonalityContainer');
    if (!container) return;
      if (this.currentData && this.currentData.values?.length) {
    container.setAttribute('data-testing-id', 'seasonality-chart');
  } else {
    container.removeAttribute('data-testing-id');
  }


    const html = `
      <div class="seasonalityMain">
        <div class="chart" ref="chartRef"></div>
    </div>
    `;
    container.innerHTML = html;
    this.drawChart();
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const chartDiv = this.shadowRoot.querySelector('.chart') as HTMLElement;
    if (!chartDiv) return;

    const instance = echarts.getInstanceByDom(chartDiv);
    if (instance) echarts.dispose(instance);

    const { months = [], values = [] } = this.currentData || {};
    if (!values.length) {
      chartDiv.innerHTML = getNoDataHTML();
      return;
    }

    const labels = months.length
      ? months
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dataValues = values.map(Number);
    const indicators = labels.map((m: any) => ({ name: m, max: 100 }));
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;

    const option = {
      tooltip: {
        show: true,
        trigger: 'item',
        ...getEChartsTooltipTheme(widgetBox),
        formatter: function (params: any) {
          // params.value is an array of all radar points
          // we need to find which point is hovered:
          const pointIndex = params?.dataIndex ?? 0;
          const series = params.value;

          // Get current mouse position relative to chart to guess nearest axis
          // ECharts does not directly expose the hovered radar point index,
          // so we use the axis name from params.name if available.
          const axisName = params.name || '';
          const idx = labels.indexOf(axisName);

          if (idx !== -1) {
            return `<b>${labels[idx]}</b>: ${series[idx]}`;
          } else {
            return labels
              .map((l: any, i: number) => `${l}: ${series[i]}`)
              .join('<br/>');
          }
        }
      },
      radar: {
        clockwise: true,
        startAngle: 120,
        indicator: indicators,
        radius: '70%',
        center: ['50%', '50%'],
        splitNumber: 5,
        axisName: { color: '#C2C2C2CC', fontSize: 14 },
        splitLine: { lineStyle: { color: '#3F3D4B', width: 1 } },
        splitArea: { show: false },
        axisLine: { lineStyle: { color: '#3F3D4B', width: 1 } }
      },
      series: [
        {
          type: 'radar',
          data: [
            {
              value: dataValues,
              name: 'Seasonality',
              itemStyle: { color: '#BD8DEF' },
              lineStyle: { color: '#BD8DEF', width: 2 },
              areaStyle: { color: '#BD8DEF', opacity: 0.3 },
              symbol: 'circle',
              symbolSize: 10,
              emphasis: {
                focus: 'self',
                itemStyle: {
                  color: '#fff',
                  borderColor: '#BD8DEF',
                  borderWidth: 3
                }
              }
            }
          ]
        }
      ]
    };

    this.currentChart = echarts.init(chartDiv);
    this.currentChart.setOption(option);

    // 🔥 Extra: show only hovered point tooltip
    this.currentChart.on('mousemove', (params: any) => {
      if (params.componentType === 'series' && params.seriesType === 'radar') {
        const dataIndex = params.dataIndex ?? 0;
        const dimIndex = params.dimensionIndex ?? params.encode?.value?.[0];
        if (typeof dimIndex === 'number') {
          const tooltipData = `${labels[dimIndex]}: ${dataValues[dimIndex]}`;
          this.currentChart.dispatchAction({
            type: 'showTip',
            seriesIndex: 0,
            name: labels[dimIndex],
          });
        }
      }
    });
  }




  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
  if (!this.shadowRoot) return;

  this.config.theme = toggleWidgetTheme(
    this.shadowRoot,
    theme
  ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

  this.updateDisplay();
}


  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseSeasonality: typeof TraderverseSeasonality; } }
if (typeof window !== 'undefined') { window.TraderverseSeasonality = TraderverseSeasonality; }
